import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deleteWMS2061_R01, disableWMS2061_R01, downloadExcel_WMS2061_R01, getPdfBlob_WMS2061_R01, getWMS2061_R01AllCount, getWMS2061_R01List, patchWMS2061_R01, postWMS2061_R01 } from "./Api_WMS2061_R01";
import { defaultWMS2061_R01Filter, WMS2061_R01Filter, useWMS2061_R01Store } from "./Store_WMS2061_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultWMS2061_R01Dto, WMS2061_R01Dto } from "./Dto_WMS2061_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, fetchFilterConfig_refetch, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import useUserStore from "src/store/useUserStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<WMS2061_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

const getWMS2061_R01GroupKey = (item: WMS2061_R01Dto) => `${item.logicCenter ?? ''}|${item.warehouseCd ?? ''}|${item.itemCd ?? ''}`;

const groupWMS2061_R01List = (list: WMS2061_R01Dto[]): WMS2061_R01Dto[] => {
	const groupMap = new Map<string, WMS2061_R01Dto>();

	list.forEach((item) => {
		const groupKey = getWMS2061_R01GroupKey(item);
		const lotQty = Number(item.lotQty) || 0;
		const updateLotQty = Number(item.updateLotQty) || 0;
		const existing = groupMap.get(groupKey);

		if (!existing) {
			groupMap.set(groupKey, {
				...defaultWMS2061_R01Dto,
				...item,
				groupKey,
				lotNo: '',
				lotQty,
				updateLotQty,
				movedLotQty: updateLotQty - lotQty,
			});
			return;
		}

		existing.lotQty += lotQty;
		existing.updateLotQty += updateLotQty;
		existing.movedLotQty = existing.updateLotQty - existing.lotQty;
	});

	// groupMap.entries() >  ["WMS10|100|ZZZ1",    { logicCenter: "WMS10", lotQty: 551, ... }],
	// Array.from(...) > ["WMS10|100|ZZZ1",    { logicCenter: "WMS10", lotQty: 551, ... }]
	// .map(([groupKey, row]) => ({ ...row, groupKey, })) > { groupKey: "WMS10|100|ZZZ1", logicCenter: "WMS10", lotQty: 551, ... }
	return Array.from(groupMap.entries()).map(([groupKey, row]) => ({ ...row, groupKey, }));
};

export const WMS2061_R01Content = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		WMS2061_R01Filter, setWMS2061_R01Filter,
		WMS2061_R01PastFilter, setWMS2061_R01PastFilter,
		WMS2061_R01List, setWMS2061_R01List, insertWMS2061_R01List, removeWMS2061_R01List,
		deletedWMS2061_R01List, addDeletedWMS2061_R01List, removeDeletedWMS2061_R01List,
		selectedWMS2061_R01List, setSelectedWMS2061_R01List, removeSelectedWMS2061_R01List,
		originalWMS2061_R01List, setOriginalWMS2061_R01List,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useWMS2061_R01Store();

	const { fetchOrgCdCode, wmsCdList, warehouseCdList } = useCommonCodeStore();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	const { company, userInfo } = useUserStore();

	const setAuth = useActionbarAuthStore((state) => state.setAuth);

	// 권한 정보 로드
	useFetchActionbarAuth("WMS2061_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedWMS2061_R01List.length === 0 && selectedWMS2061_R01List.length === 0,
			save: WMS2061_R01List.length === 0,
			print: WMS2061_R01List.length === 0,
			excel: WMS2061_R01List.length === 0
		}));
	}, [selectedWMS2061_R01List, deletedWMS2061_R01List, WMS2061_R01List]);

	const validateInsertData = (data: WMS2061_R01Dto, currentRowIndex: number) => {

		if(data.updateLotQty && data.updateLotQty < 0) {
			showWarning('실재고는 0보다 작을 수 없습니다.'); return false;
		}

		return true;
	}

	const getParams = (filter: WMS2061_R01Filter) => {
		const params: Partial<WMS2061_R01Filter> = {
			logicCenter: filter.logicCenter || undefined,
			warehouseCd: filter.warehouseCd || undefined,
			brandCd: filter.brandCd || undefined,
			itemCd: filter.itemCd || undefined,
		};
		return params;
	}
	
	//신규 행 추가시 기본적으로 세팅할 데이터를 정하고 생성
	const defaultWMS2061_R01Row = useCallback(() => {

		return {
			...defaultWMS2061_R01Dto,
			inserted: true,
		}
	}, [defaultWMS2061_R01Dto, wmsCdList, warehouseCdList]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getWMS2061_R01AllCount(getParams(WMS2061_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [WMS2061_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);

		if (fetchType === 'search' || messageMode === 'show') {
			setScrollTop(null);
		}
	
		let initialListLength = WMS2061_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setWMS2061_R01List([]);
		}

		const responseData = await getWMS2061_R01List(
			'',
			getParams(WMS2061_R01Filter),
			fetchType === 'refetch' ? initialListLength : WMS2061_R01List.length ?? 0
		);
	
		const rawList = [...responseData.data.data] as WMS2061_R01Dto[];
		const newList = groupWMS2061_R01List(rawList);
		setWMS2061_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalWMS2061_R01List(newList);
		setSelectedWMS2061_R01List([]);
		removeDeletedWMS2061_R01List(deletedWMS2061_R01List);
	
		if (newList.length === 0) {
			insertWMS2061_R01List([{ ...defaultWMS2061_R01Row(), inserted: true }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else {
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}
		}
	
		setWMS2061_R01PastFilter(WMS2061_R01Filter);
		setLoadingSpinner(false);
	}, [
		WMS2061_R01Filter,  WMS2061_R01PastFilter, WMS2061_R01List, deletedWMS2061_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalWMS2061_R01List, setSelectedWMS2061_R01List, insertWMS2061_R01List,
		defaultWMS2061_R01Row, setScrollTop,
	]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);
	
	
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_WMS2061_R01(
			'',
			getParams(WMS2061_R01Filter),
			WMS2061_R01List.length ?? 0
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '생산계획 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [WMS2061_R01Filter, WMS2061_R01List])

	

	const handlePrintClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_WMS2061_R01(
				'',
				getParams(WMS2061_R01Filter),
				WMS2061_R01List.length ?? 0,
				company?.compCd || ''
			);

			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [WMS2061_R01Filter, WMS2061_R01List, company?.compCd, printPdfFromBlob, setLoadingSpinner, setMessage]);
	
	
	const handleSaveClick = useCallback(async () => {

		setLoadingSpinner(true);

		// 신규 사용자 처리
		const insertedList: WMS2061_R01Dto[] = WMS2061_R01List.filter(row => 
			!deletedWMS2061_R01List.includes(row.lotNo) && 
			row.inserted === true
		);

		// 수정된 사용자만 필터링
		const modifiedList = WMS2061_R01List.filter(row => 
			!deletedWMS2061_R01List.includes(row.lotNo) && 
			row.isModified && !row.inserted
		);

		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = WMS2061_R01List.findIndex(user => user.lotNo === value.lotNo);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.lotNo);
		});
		if(!valid) { setLoadingSpinner(false); return; }
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();
		valid = true;
		insertedList.forEach((value) => {
			let index = WMS2061_R01List.findIndex(user => user.lotNo === value.lotNo);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.lotNo);
		});
		if(!valid) { setLoadingSpinner(false); return; }

		const deleteStringList = deletedWMS2061_R01List.map(item => item.toString());
		const deleteTargetList = WMS2061_R01List.filter(item => deleteStringList.includes(item.valueIndex.toString()));

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postWMS2061_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchWMS2061_R01(modifiedList) : Promise.resolve(),
			deletedWMS2061_R01List.length > 0 ? Promise.all(deleteTargetList.map(value => deleteWMS2061_R01(value.lotNo))) : Promise.resolve()
		]);

		if(insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('로케이션정보 저장이 완료되었습니다.');
			setMessage('로케이션정보 저장이 완료되었습니다.');
		} else if(deleteResponse) {
			showSuccess('로케이션정보 삭제가 완료되었습니다.');
			setMessage('로케이션정보 삭제가 완료되었습니다.');
		}
		setLoadingSpinner(false);


		setTimeout(() => { setMessage(''); }, 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });

	}, [WMS2061_R01List, deletedWMS2061_R01List, originalWMS2061_R01List, executeDataFetch]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedWMS2061_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedWMS2061_R01List(deletedWMS2061_R01List);
			removeSelectedWMS2061_R01List();
			setWMS2061_R01List([...WMS2061_R01List]);
		} else if (selectedWMS2061_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedWMS2061_R01List(selectedWMS2061_R01List);
			removeSelectedWMS2061_R01List(selectedWMS2061_R01List);
		}
	}, [selectedWMS2061_R01List, deletedWMS2061_R01List, addDeletedWMS2061_R01List, removeDeletedWMS2061_R01List, removeSelectedWMS2061_R01List, setWMS2061_R01List, WMS2061_R01List]);

	return{
		defaultWMS2061_R01Row,
		fetchData,
		initContainerData,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePrintClick,
		buttonDisabled,
	}
}
